import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionCard from '@/components/feature/SectionCard';
import TeamBadge from '@/components/base/TeamBadge';
import NewsRow from '@/components/feature/NewsRow';
import MatchHeader from '@/pages/detail/components/MatchHeader';
import StatsCompare from '@/pages/detail/components/StatsCompare';
import EventTimeline from '@/pages/detail/components/EventTimeline';
import { useSEO } from '@/hooks/useSEO';
import { allNews, getMatch, getNewsByMatch, getTeam } from '@/utils/data';

export default function MatchDetailPage() {
  const { matchId } = useParams();
  const match = getMatch(matchId);
  const home = getTeam(match?.homeTeamId);
  const away = getTeam(match?.awayTeamId);

  useSEO({
    title: match ? `${home?.name} vs ${away?.name} 比赛详情 - 篮球比分` : '比赛不存在 - 篮球比分',
    description: match
      ? `${match.leagueName}${match.stage}，${home?.name}对阵${away?.name}的比分、技术统计与比赛事件汇总。`
      : '未找到对应的比赛信息。',
    keywords: match ? `${home?.name},${away?.name},篮球比赛详情,篮球比分` : '篮球比赛',
  });

  if (!match || !home || !away) {
    return (
      <div className="px-4 md:px-6 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-900">比赛不存在或已下线</h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-md">您访问的比赛可能已被移除，或者链接有误。返回首页查看更多赛事。</p>
        <Link to="/" className="mt-6 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center cursor-pointer whitespace-nowrap">
          返回首页
        </Link>
      </div>
    );
  }

  const relatedNews = (() => {
    const byMatch = getNewsByMatch(match.id);
    if (byMatch.length > 0) return byMatch;
    const byTeam = allNews.filter((n) => n.relatedTeamIds.includes(home.id) || n.relatedTeamIds.includes(away.id));
    return byTeam.length > 0 ? byTeam : allNews.slice(0, 3);
  })();

  return (
    <div className="px-4 md:px-6 py-6 animate-fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '赛事', to: '/category/all' },
            { label: match.leagueName, to: `/category/${match.leagueId}` },
            { label: `${home.name} vs ${away.name}` },
          ]}
        />
        <Link
          to={`/category/${match.leagueId}`}
          className="flex items-center gap-1 h-9 px-3 rounded-md border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回赛事列表
        </Link>
      </div>

      <div className="mt-4 space-y-6">
        <MatchHeader match={match} />

        {match.quarterScores.length > 0 && (
          <SectionCard title="各节比分" icon="ri-list-ordered">
            <div className="px-4 md:px-5 py-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-foreground-500">
                    <th className="text-left font-normal pb-2">球队</th>
                    {match.quarterScores.map((q) => (
                      <th key={q.quarter} className="font-normal pb-2 text-center">{q.quarter}</th>
                    ))}
                    <th className="text-right font-normal pb-2">总分</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-background-200/70">
                    <td className="py-2.5">
                      <span className="flex items-center gap-2">
                        <TeamBadge team={home} size="sm" />
                        <span className="text-foreground-800 whitespace-nowrap">{home.name}</span>
                      </span>
                    </td>
                    {match.quarterScores.map((q) => (
                      <td key={q.quarter} className="py-2.5 text-center tabular-nums text-foreground-700">{q.home}</td>
                    ))}
                    <td className="py-2.5 text-right tabular-nums font-heading font-bold text-primary-600">{match.homeScore}</td>
                  </tr>
                  <tr className="border-t border-background-200/70">
                    <td className="py-2.5">
                      <span className="flex items-center gap-2">
                        <TeamBadge team={away} size="sm" />
                        <span className="text-foreground-800 whitespace-nowrap">{away.name}</span>
                      </span>
                    </td>
                    {match.quarterScores.map((q) => (
                      <td key={q.quarter} className="py-2.5 text-center tabular-nums text-foreground-700">{q.away}</td>
                    ))}
                    <td className="py-2.5 text-right tabular-nums font-heading font-bold text-secondary-600">{match.awayScore}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </SectionCard>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <SectionCard title="数据统计" icon="ri-bar-chart-2-line">
              <StatsCompare match={match} />
            </SectionCard>

            <SectionCard title="比赛事件" icon="ri-time-line">
              <EventTimeline match={match} />
            </SectionCard>
          </div>

          <aside className="lg:col-span-2 space-y-6">
            <SectionCard title="对阵球队" icon="ri-team-line">
              <div className="p-4 space-y-3">
                {[home, away].map((t) => (
                  <Link
                    key={t.id}
                    to={`/team/${t.id}`}
                    className="flex items-center gap-3 p-3 rounded-md border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
                  >
                    <TeamBadge team={t} size="md" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{t.name}</p>
                      <p className="text-xs text-foreground-500 truncate">{t.leagueName} · 排名第 {t.rank} 位</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </Link>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="相关新闻" icon="ri-newspaper-line" moreTo="/news" moreText="更多新闻">
              <div>
                {relatedNews.slice(0, 4).map((n) => (
                  <NewsRow key={n.id} news={n} />
                ))}
              </div>
            </SectionCard>
          </aside>
        </div>
      </div>
    </div>
  );
}